import React, { useState, useEffect, useCallback, useContext } from 'react'
import { useSelector } from 'react-redux'
import axios from 'axios'

import AppConfigContext from '../app_layout/app_config_context'
import TablePagination from '../widgets/pagination'
import showToastNotification from '../widgets/toastify'
import LoadingWrapper from '../widgets/loading'
import TrialPeriodsTable from './trial_periods_table'
import TrialActionModal from './trial_action_modal'
import { listTrials, fetchMetrics, extendTrial, expireTrial, reenableTrial } from './helpers/trial_service'
import { PRODUCTS } from './helpers/trial_data'

import '../../static/css/trial_periods.css'

const PAGE_SIZE = 8

const METRIC_DEFS = [
  { key: 'offered', label: 'Trials Offered', accent: '#353a44', icon: 'fa-layer-group' },
  { key: 'active', label: 'Currently Active', accent: '#198754', icon: 'fa-circle-play' },
  { key: 'expiringSoon', label: 'Expiring Soon', accent: '#e48725', icon: 'fa-hourglass-half' },
  { key: 'expired', label: 'Expired', accent: '#a3a3a3', icon: 'fa-circle-xmark' },
]

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'expired', label: 'Expired' },
]

const ACTIONS = { extend: extendTrial, expire: expireTrial, reenable: reenableTrial }

const TrialPeriods = () => {
  const appConfig = useContext(AppConfigContext)
  const client = appConfig.client
  const operatorId = useSelector((state) => state.user.id)

  const [rows, setRows] = useState([])
  const [count, setCount] = useState(0)
  const [metrics, setMetrics] = useState({ offered: 0, active: 0, expiringSoon: 0, expired: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [product, setProduct] = useState('data_pulse')
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(0)
  const [modal, setModal] = useState(null)
  const [busy, setBusy] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  // Debounce the search box.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300)
    return () => clearTimeout(timer)
  }, [query])

  // Reset to the first page whenever the result set changes shape.
  useEffect(() => { setPage(0) }, [debouncedQuery, statusFilter, product])

  // Load the table page (server-side: skip / limit / search / status).
  useEffect(() => {
    const controller = new AbortController()
    setIsLoading(true)
    listTrials(client, {
      product,
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE,
      search: debouncedQuery,
      status: statusFilter,
    }, controller.signal)
      .then(({ rows: data, count: total }) => {
        setRows(data)
        setCount(total)
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          showToastNotification({ message: 'Failed to load trials.', isSuccess: false })
        }
      })
      .finally(() => setIsLoading(false))
    return () => controller.abort('canceled')
  }, [client, product, page, debouncedQuery, statusFilter, reloadKey])

  // Load service-level metrics (independent of the table page).
  useEffect(() => {
    const controller = new AbortController()
    fetchMetrics(client, { product }, controller.signal)
      .then((m) => setMetrics(m))
      .catch((error) => {
        if (!axios.isCancel(error)) setMetrics({ offered: 0, active: 0, expiringSoon: 0, expired: 0 })
      })
    return () => controller.abort('canceled')
  }, [client, product, reloadKey])

  const statusCount = (key) => {
    if (key === 'active') return metrics.active
    if (key === 'expired') return metrics.expired
    return metrics.offered
  }

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)

  const onConfirm = useCallback(({ mode, days, reason }) => {
    if (!modal || !modal.trial) return
    const trial = modal.trial
    setBusy(true)
    ACTIONS[mode](client, { coUserId: trial.co_user_id, operatorId, days, reason })
      .then(() => {
        const verb = {
          extend: 'extended by ' + days + ' days',
          expire: 'expired',
          reenable: 're-enabled for ' + days + ' days',
        }[mode]
        showToastNotification({ message: trial.name + '’s trial ' + verb + '.', isSuccess: true })
        setModal(null)
        setReloadKey((k) => k + 1)
      })
      .catch((error) => {
        showToastNotification({ message: error.message || 'Could not complete the action. Please try again.', isSuccess: false })
      })
      .finally(() => setBusy(false))
  }, [client, operatorId, modal])

  return (
    <div className='cocrm-trial-periods overflow-auto'>
      <div>
        <h1 className='co-page-title'>Trial Periods</h1>
        <p className='co-page-sub'>Manage Data Pulse free trials &mdash; extend, expire or re-enable, and track service levels.</p>
      </div>
      {/* Product selector */}
      <div
        className='tp-products'
        role='tablist'
        aria-label='Product'
      >
        {PRODUCTS.map((p) => (
          <button
            key={p.key}
            role='tab'
            disabled={!p.enabled}
            aria-selected={product === p.key}
            className={'tp-product' + (product === p.key ? ' active' : '') + (p.enabled ? '' : ' disabled')}
            onClick={() => p.enabled && setProduct(p.key)}
          >
            <i className={'fa-solid ' + p.icon} />
            <span>{p.label}</span>
            {!p.enabled && <span className='tp-product-soon'>Soon</span>}
          </button>
        ))}
      </div>
      {/* SLA metrics */}
      <div className='tp-metric-cards'>
        {METRIC_DEFS.map((m) => (
          <div
            key={m.key}
            className='tp-stat-card'
            style={{ borderLeftColor: m.accent }}
          >
            <span
              className='tp-stat-icon'
              style={{ color: m.accent }}
            >
              <i className={'fa-solid ' + m.icon} />
            </span>
            <div className='tp-stat-text'>
              <div
                className='tp-stat-value'
                style={{ color: m.accent }}
              >
                {metrics[m.key]}
              </div>
              <div className='tp-stat-label'>{m.label}</div>
            </div>
          </div>
        ))}
      </div>
      {/* Toolbar */}
      <div className='tp-toolbar'>
        <div className='tp-statusfilters'>
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.key}
              className={'tp-statusfilter' + (statusFilter === s.key ? ' active' : '')}
              onClick={() => setStatusFilter(s.key)}
            >
              {s.label}
              <span className='tp-statusfilter-count'>{statusCount(s.key)}</span>
            </button>
          ))}
        </div>
        <div className='tp-toolbar-right'>
          <div className='tp-search'>
            <i className='fa-solid fa-magnifying-glass tp-search-icon' />
            <input
              value={query}
              placeholder='Search by name, email or organization…'
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button
            type='button'
            className='tp-refresh-btn'
            title='Refresh'
            aria-label='Refresh'
            disabled={isLoading}
            onClick={() => setReloadKey((k) => k + 1)}
          >
            <i className={'fa-solid fa-rotate-right' + (isLoading ? ' fa-spin' : '')} />
          </button>
        </div>
      </div>
      {/* Table */}
      {isLoading ? (
        <div className='d-flex justify-content-center py-5'>
          <LoadingWrapper
            type='spin'
            color='#9aa0a6'
            height={40}
            width={40}
          />
        </div>
      ) : (
        <TrialPeriodsTable
          rows={rows}
          client={client}
          onAction={(mode, trial) => setModal({ mode, trial })}
        />
      )}
      {/* Footer: count + pagination */}
      <div className='tp-table-foot'>
        <span className='tp-result-count'>
          {count === 0
            ? 'No results'
            : 'Showing ' + (safePage * PAGE_SIZE + 1) + '–' + Math.min(count, safePage * PAGE_SIZE + PAGE_SIZE) + ' of ' + count}
        </span>
        {totalPages > 1 && (
          <TablePagination
            currentPageIndex={safePage}
            totalPage={totalPages}
            updateTableData={setPage}
          />
        )}
      </div>
      {modal && (
        <TrialActionModal
          mode={modal.mode}
          trial={modal.trial}
          busy={busy}
          onClose={() => setModal(null)}
          onConfirm={onConfirm}
        />
      )}
    </div>
  )
}

export default TrialPeriods
